import React from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { useAuth } from "../auth/authStore";
import { filesApi } from "../api/filesApi";
import { podcastsApi } from "../api/podcastsApi";
import AppSidebarV2 from "../components/layout/AppSidebarV2";
import { useNotifications } from "../notifications/notificationsStore";
import { extractTotal } from "../subscription/subscriptionLimits";
import { useCreateSectionGate } from "../subscription/useCreateSectionGate";
import { useToast } from "../ui/toast/ToastProvider";
import { getSiteOrigin, joinUrl } from "../utils/seo";

const MAX_COVER_SIZE = 5 * 1024 * 1024;
const TOPIC_OPTIONS = ["Тревога", "Отношения", "Карьера", "Самопознание", "Травма", "Семья", "Депрессия"];
const LANGUAGE_OPTIONS = ["Русский", "Английский", "Украинский"];

function asObject(value) {
  return value && typeof value === "object" ? value : {};
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function asText(value, max = 2000) {
  return String(value || "").trim().slice(0, max);
}

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function firstNonEmpty(...values) {
  for (const value of values) {
    const text = asText(value, 1000);
    if (text) return text;
  }
  return "";
}

function normalizePage(payload) {
  const raw = asObject(payload);
  const unwrapped = raw.item ?? raw.items ?? raw.data ?? raw;
  if (Array.isArray(unwrapped)) return unwrapped;
  const page = asObject(unwrapped);
  return asArray(page.content || page.items || page.data || page.records);
}

function formatInt(value) {
  return new Intl.NumberFormat("ru-RU").format(Math.max(0, toNumber(value, 0)));
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("ru-RU", { day: "2-digit", month: "short", year: "numeric" }).format(date);
}

function formatDuration(secondsValue) {
  const seconds = Math.round(Number(secondsValue));
  if (!Number.isFinite(seconds) || seconds <= 0) return "—";
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return rest ? `${minutes}:${String(rest).padStart(2, "0")}` : `${minutes} мин`;
}

function initials(name) {
  const parsed = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((chunk) => chunk[0])
    .join("");
  return parsed ? parsed.toUpperCase() : "BS";
}

function extractUploadedUrl(uploaded) {
  const raw = asObject(uploaded);
  return firstNonEmpty(
    raw.url,
    raw.fileUrl,
    raw.imageUrl,
    raw.coverUrl,
    raw.path,
    raw.file?.url,
    raw.file?.fileUrl,
    raw.item?.url,
    raw.item?.fileUrl,
    raw.item?.file?.url,
    raw.data?.url,
    raw.data?.fileUrl,
    raw.data?.file?.url
  );
}

function normalizeTags(raw = {}) {
  const tags = raw.tags || raw.topicTags || raw.hashtags || raw.topics || [];
  if (Array.isArray(tags)) return tags.map((item) => asText(item, 40).replace(/^#+/, "")).filter(Boolean).slice(0, 12);
  return String(tags || "")
    .split(/[#,]/)
    .map((item) => asText(item, 40).replace(/^#+/, ""))
    .filter(Boolean)
    .slice(0, 12);
}

function podcastToForm(podcast = {}, fallbackAuthor = "") {
  const raw = asObject(podcast);
  const tags = normalizeTags(raw);
  const topic = firstNonEmpty(raw.topic, asArray(raw.topics)[0], asArray(raw.topicLabels)[0], tags[0]);
  const published =
    raw.published !== undefined
      ? Boolean(raw.published)
      : raw.isPublished !== undefined
        ? Boolean(raw.isPublished)
        : raw.status
          ? String(raw.status).toUpperCase() === "PUBLISHED"
          : true;

  return {
    title: firstNonEmpty(raw.title, raw.name),
    shortDescription: firstNonEmpty(raw.shortDescription, raw.subtitle, raw.description),
    description: firstNonEmpty(raw.description, raw.shortDescription, raw.subtitle),
    topic,
    language: firstNonEmpty(raw.language, raw.locale, "Русский"),
    authorName: firstNonEmpty(raw.authorName, raw.author, raw.psychologistName, fallbackAuthor),
    coverEmoji: firstNonEmpty(raw.coverEmoji, raw.emoji, "🎙️"),
    coverUrl: firstNonEmpty(raw.coverImageUrl, raw.coverUrl, raw.imageUrl, raw.artworkUrl),
    releaseFrequency: firstNonEmpty(raw.releaseFrequency, raw.frequency, "Еженедельно"),
    status: published ? "PUBLISHED" : "DRAFT",
    showInCatalog: raw.showInCatalog !== undefined ? Boolean(raw.showInCatalog) : published,
    tags,
  };
}

function normalizePodcast(raw = {}) {
  const item = asObject(raw);
  return {
    id: item.id ?? item.podcastId ?? "",
    slug: firstNonEmpty(item.slug),
    title: firstNonEmpty(item.title, item.name, "Подкаст"),
    shortDescription: firstNonEmpty(item.shortDescription, item.description, "Описание подкаста пока не заполнено."),
    coverEmoji: firstNonEmpty(item.coverEmoji, item.emoji, "🎙️"),
    coverImageUrl: firstNonEmpty(item.coverImageUrl, item.coverUrl, item.imageUrl, item.artworkUrl),
    episodesCount: toNumber(item.episodesCount ?? item.totalEpisodes, 0),
    subscribersCount: toNumber(item.subscribersCount ?? item.followersCount, 0),
    listensCount: toNumber(item.listensCount ?? item.totalListens ?? item.playsCount, 0),
    published: item.published !== undefined ? Boolean(item.published) : String(item.status || "PUBLISHED").toUpperCase() !== "DRAFT",
    updatedAt: item.updatedAt || item.modifiedAt || item.createdAt || null,
  };
}

function normalizeEpisode(raw = {}, idx = 0, podcast = {}) {
  const item = asObject(raw);
  const status = String(item.status || (item.published ? "PUBLISHED" : "DRAFT") || "DRAFT").toUpperCase();
  return {
    id: item.id ?? item.episodeId ?? "",
    number: item.episodeNumber ?? item.number ?? idx + 1,
    title: firstNonEmpty(item.title, item.name, "Эпизод без названия"),
    coverImageUrl: firstNonEmpty(item.coverImageUrl, item.coverUrl, item.imageUrl, podcast.coverImageUrl),
    coverEmoji: firstNonEmpty(item.coverEmoji, podcast.coverEmoji, "🎙️"),
    duration: item.durationSeconds ?? (item.durationMinutes ? Number(item.durationMinutes) * 60 : 0),
    date: item.publishedAt || item.publishDate || item.createdAt || item.updatedAt || null,
    listens: toNumber(item.listensCount ?? item.totalListens ?? item.playCount, 0),
    status,
  };
}

function StatusCheck({ ok, children }) {
  return <li className={ok ? "is-ok" : ""}>{children}</li>;
}

export default function PsychologistPodcastBuilder() {
  const { podcastId: routePodcastId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const { ensureWithinLimit, checkCreateAccess, limitModal } = useCreateSectionGate();
  const { role, me, logout } = useAuth();
  const { unreadNotificationsCount } = useNotifications();
  const coverInputRef = React.useRef(null);

  const canManage = role === "PSYCHOLOGIST" || role === "ADMIN" || role === "MODERATOR";
  const statePodcast = location.state?.sourcePodcast || null;
  const queryPodcastId = React.useMemo(() => new URLSearchParams(location.search || "").get("podcastId") || "", [location.search]);
  const podcastId = routePodcastId || queryPodcastId || statePodcast?.id || statePodcast?.podcastId || "";
  const isEditing = Boolean(podcastId);

  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(Boolean(isEditing && !statePodcast));
  const [episodesLoading, setEpisodesLoading] = React.useState(Boolean(isEditing));
  const [saving, setSaving] = React.useState(false);
  const [uploadingCover, setUploadingCover] = React.useState(false);
  const [coverFileName, setCoverFileName] = React.useState("");
  const [podcast, setPodcast] = React.useState(() => (statePodcast ? normalizePodcast(statePodcast) : null));
  const [episodes, setEpisodes] = React.useState([]);
  const [tagInput, setTagInput] = React.useState("");
  const [form, setForm] = React.useState(() => podcastToForm(statePodcast || {}, me?.name || me?.login || ""));
  const [entryCheckDone, setEntryCheckDone] = React.useState(() => isEditing);
  const [entryAllowed, setEntryAllowed] = React.useState(true);

  const origin = React.useMemo(() => getSiteOrigin(), []);
  const canonical = React.useMemo(
    () => joinUrl(origin, isEditing ? `/dashboard/podcasts/${encodeURIComponent(String(podcastId))}/edit` : "/dashboard/podcasts/new"),
    [origin, isEditing, podcastId]
  );

  React.useEffect(() => {
    if (!canManage || !isEditing) {
      setLoading(false);
      return undefined;
    }

    let alive = true;
    setLoading(true);
    podcastsApi
      .getMyPodcast(podcastId)
      .then((loaded) => {
        if (!alive) return;
        const normalized = normalizePodcast(loaded || {});
        setPodcast(normalized);
        setForm(podcastToForm(loaded || {}, me?.name || me?.login || ""));
      })
      .catch((err) => toast.error(err?.message || "Не удалось загрузить подкаст"))
      .finally(() => alive && setLoading(false));

    return () => {
      alive = false;
    };
  }, [canManage, isEditing, me?.login, me?.name, podcastId, statePodcast, toast]);

  React.useEffect(() => {
    if (!canManage || !isEditing || !podcastId) {
      setEpisodes([]);
      setEpisodesLoading(false);
      return undefined;
    }

    let alive = true;
    setEpisodesLoading(true);
    podcastsApi
      .getMyPodcastEpisodes(podcastId, { page: 0, size: 100 })
      .then((payload) => {
        if (!alive) return;
        const currentPodcast = podcast || normalizePodcast(statePodcast || {});
        setEpisodes(normalizePage(payload).map((item, idx) => normalizeEpisode(item, idx, currentPodcast)));
      })
      .catch((err) => {
        if (!alive) return;
        setEpisodes([]);
        toast.error(err?.message || "Не удалось загрузить эпизоды подкаста");
      })
      .finally(() => alive && setEpisodesLoading(false));

    return () => {
      alive = false;
    };
  }, [canManage, isEditing, podcast, podcastId, statePodcast, toast]);

  React.useEffect(() => {
    let alive = true;
    if (!canManage) {
      setEntryAllowed(false);
      setEntryCheckDone(true);
      return undefined;
    }

    if (isEditing) {
      setEntryAllowed(true);
      setEntryCheckDone(true);
      return undefined;
    }

    (async () => {
      const allowed = await checkCreateAccess("/dashboard/podcasts/new");
      if (!alive) return;
      setEntryAllowed(Boolean(allowed));
      setEntryCheckDone(true);
    })();

    return () => {
      alive = false;
    };
  }, [canManage, checkCreateAccess, isEditing]);

  const setField = (name) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const addTag = React.useCallback((value = tagInput) => {
    const normalized = asText(value, 40).replace(/^#+/, "");
    if (!normalized) return;
    setForm((current) => {
      const next = [...asArray(current.tags)];
      if (!next.some((item) => item.toLowerCase() === normalized.toLowerCase())) next.push(normalized);
      return { ...current, tags: next.slice(0, 12) };
    });
    setTagInput("");
  }, [tagInput]);

  const removeTag = (tag) => {
    setForm((current) => ({ ...current, tags: asArray(current.tags).filter((item) => item !== tag) }));
  };

  const uploadCoverFile = async (file) => {
    if (!file) return;
    if (file.size > MAX_COVER_SIZE) {
      toast.error("Обложка больше 5 МБ");
      return;
    }
    setUploadingCover(true);
    setCoverFileName(file.name);
    try {
      const uploaded = await filesApi.uploadImage(file);
      const uploadedUrl = extractUploadedUrl(uploaded);
      if (!uploadedUrl) throw new Error("Сервер не вернул URL изображения");
      setForm((current) => ({ ...current, coverUrl: uploadedUrl }));
      toast.success("Обложка подкаста загружена");
    } catch (err) {
      toast.error(err?.message || "Не удалось загрузить обложку");
    } finally {
      setUploadingCover(false);
    }
  };

  const buildPayload = (forcedStatus = form.status) => {
    const status = String(forcedStatus || "DRAFT").toUpperCase();
    const cleanTags = asArray(form.tags).map((item) => asText(item, 40).replace(/^#+/, "")).filter(Boolean);
    const topic = asText(form.topic, 80);
    const topics = [...new Set([topic, ...cleanTags].filter(Boolean))].slice(0, 12);

    return {
      title: asText(form.title, 180),
      name: asText(form.title, 180),
      shortDescription: asText(form.shortDescription, 500) || null,
      description: asText(form.description, 4000) || asText(form.shortDescription, 500) || null,
      topic: topic || null,
      topics,
      tags: cleanTags,
      language: asText(form.language, 40) || "Русский",
      authorName: asText(form.authorName, 120) || null,
      coverEmoji: asText(form.coverEmoji, 8) || "🎙️",
      coverImageUrl: asText(form.coverUrl, 1000) || null,
      coverUrl: asText(form.coverUrl, 1000) || null,
      imageUrl: asText(form.coverUrl, 1000) || null,
      releaseFrequency: asText(form.releaseFrequency, 120) || null,
      status,
      published: status === "PUBLISHED",
      showInCatalog: status === "PUBLISHED" && Boolean(form.showInCatalog),
    };
  };

  const savePodcast = async (status = form.status, { addEpisodeAfter = false } = {}) => {
    const payload = buildPayload(status);
    if (!payload.title) {
      toast.error("Укажите название подкаста");
      return;
    }

    if (!isEditing) {
      try {
        const listRaw = await podcastsApi.getMyPodcasts({ page: 0, size: 1 });
        const usage = extractTotal(listRaw, normalizePage(listRaw).length);
        const allowed = ensureWithinLimit({ featureKey: "podcasts", usage });
        if (!allowed) return;
      } catch (limitError) {
        toast.error(limitError?.message || "Не удалось проверить лимит подписки");
        return;
      }
    }

    setSaving(true);
    try {
      const saved = isEditing
        ? await podcastsApi.updateMyPodcast(podcastId, payload)
        : await podcastsApi.createMyPodcast(payload);
      const savedId = saved?.id || saved?.podcastId || saved?.item?.id || saved?.data?.id || podcastId;
      toast.success(isEditing ? "Подкаст сохранён" : "Подкаст создан");

      if (addEpisodeAfter && savedId) {
        navigate(`/dashboard/podcasts/${encodeURIComponent(String(savedId))}/episodes/new`, { state: { podcastId: savedId } });
        return;
      }
      if (!isEditing && savedId) {
        navigate(`/dashboard/podcasts/${encodeURIComponent(String(savedId))}/edit`, { replace: true, state: { sourcePodcast: saved?.item || saved?.data || saved } });
        return;
      }
      if (isEditing) {
        const normalized = normalizePodcast({ ...(podcast || {}), ...payload, id: podcastId, ...asObject(saved) });
        setPodcast(normalized);
      }
    } catch (err) {
      toast.error(err?.message || "Не удалось сохранить подкаст");
    } finally {
      setSaving(false);
    }
  };

  const openEpisodeBuilder = (episode = null) => {
    const id = podcastId || podcast?.id;
    if (!id) {
      toast.info("Сначала сохраните подкаст, потом добавьте первый эпизод");
      return;
    }
    if (episode?.id) {
      navigate(`/dashboard/podcasts/${encodeURIComponent(String(id))}/episodes/${encodeURIComponent(String(episode.id))}/edit`, {
        state: { podcastId: id, sourceEpisode: episode, sourceEpisodeId: episode.id },
      });
      return;
    }
    navigate(`/dashboard/podcasts/${encodeURIComponent(String(id))}/episodes/new`, { state: { podcastId: id } });
  };

  const openPublicPodcast = () => {
    const publicId = podcast?.slug || podcastId;
    if (!publicId) return;
    navigate(`/podcasts/${encodeURIComponent(String(publicId))}`);
  };

  const coverPreview = form.coverUrl || "";
  const published = form.status === "PUBLISHED";

  if (!canManage) {
    return (
      <section className="dashboard-placeholder" style={{ marginBottom: 24 }}>
        <div className="dashboard-placeholder__hero">
          <h3>Доступ ограничен</h3>
          <p>Управление подкастами доступно только психологам и администраторам.</p>
        </div>
        <div className="dashboard-placeholder__actions">
          <Link className="dashboard__btn dashboard__btn--ghost" to="/dashboard">
            ← Вернуться в кабинет
          </Link>
        </div>
      </section>
    );
  }

  if (!entryCheckDone) {
    return <div className="podcasts-hub" />;
  }

  if (!entryAllowed) {
    return (
      <>
        <section className="dashboard-placeholder" style={{ marginBottom: 24 }}>
          <div className="dashboard-placeholder__hero">
            <h3>Лимит достигнут</h3>
            <p>Для создания новых подкастов перейдите на тариф выше в подписке.</p>
          </div>
          <div className="dashboard-placeholder__actions">
            <Link className="dashboard__btn dashboard__btn--ghost" to="/dashboard/podcasts">
              ← К моим подкастам
            </Link>
          </div>
        </section>
        {limitModal}
      </>
    );
  }

  return (
    <div className="podcasts-hub">
      <Helmet prioritizeSeoTags>
        <title>{isEditing ? "Редактирование подкаста" : "Новый подкаст"} | Бюро Души</title>
        {canonical ? <link rel="canonical" href={canonical} /> : null}
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="podcasts-shell">
        <AppSidebarV2
          activeKey="podcasts"
          mobileOpen={sidebarOpen}
          userName={me?.name || me?.login || "Гость"}
          roleLabel={role || "PSYCHOLOGIST"}
          notificationsBadge={unreadNotificationsCount}
          onCloseMobile={() => setSidebarOpen(false)}
          onLogout={logout}
        />

        <button
          className={`podcasts-overlay ${sidebarOpen ? "is-open" : ""}`}
          type="button"
          aria-label="Закрыть меню"
          onClick={() => setSidebarOpen(false)}
        />

        <div className="podcasts-main">
          <header className="podcasts-topbar">
            <div className="podcasts-topbar__left">
              <button className="podcasts-topbar__menu-btn" type="button" onClick={() => setSidebarOpen(true)} aria-label="Открыть меню">
                ☰
              </button>
              <div className="podcasts-topbar__search podcast-editor-v3__top-current">
                <span>{isEditing ? form.title || "Редактирование подкаста" : "Сначала создаём подкаст"}</span>
              </div>
            </div>
            <div className="podcasts-topbar__right">
              <button className="podcasts-btn podcasts-btn--subtle" type="button" onClick={() => navigate("/dashboard/podcasts")}>Мои подкасты</button>
              {isEditing ? <button className="podcasts-btn podcasts-btn--subtle" type="button" onClick={openPublicPodcast}>Публичная страница</button> : null}
              <button className="podcasts-btn podcasts-btn--mint" type="button" disabled={saving || loading || uploadingCover} onClick={() => savePodcast("PUBLISHED")}>{saving ? "Сохраняем..." : "Сохранить подкаст"}</button>
              <div className="podcasts-topbar__separator" />
              <button className="podcasts-topbar__avatar" type="button" onClick={() => navigate("/dashboard/settings")}>{initials(me?.name || me?.login)}</button>
            </div>
          </header>

          <main className="podcasts-content podcast-editor-v3">
            <div className="podcast-editor-v3__crumbs">
              <button type="button" onClick={() => navigate("/dashboard/podcasts")}>Мои подкасты</button>
              <span aria-hidden="true">›</span>
              <span>{isEditing ? form.title || "Редактирование подкаста" : "Новый подкаст"}</span>
            </div>

            <header className="podcast-editor-v3__head">
              <div>
                <h1>{isEditing ? "Редактирование подкаста" : "Новый подкаст"}</h1>
                <p>
                  Сначала создайте карточку подкаста: название, описание, обложку, темы и статус. После сохранения в этом же редакторе появится список выпусков и кнопка добавления эпизода.
                </p>
              </div>
              <div className="podcast-editor-v3__head-actions">
                <button type="button" className="podcast-editor-v3__ghost-btn" disabled={saving || loading} onClick={() => savePodcast("DRAFT")}>Сохранить черновик</button>
                <button type="button" className="podcast-editor-v3__mint-btn" disabled={saving || loading || uploadingCover} onClick={() => savePodcast(form.status)}>{saving ? "Сохраняем..." : "Сохранить подкаст"}</button>
              </div>
            </header>

            {loading ? <div className="podcasts-empty podcasts-empty--inline">Загружаем подкаст…</div> : null}

            <div className="podcast-editor-v3__layout">
              <div className="podcast-editor-v3__left">
                <section className="podcast-editor-v3__card">
                  <div className="podcast-editor-v3__section-title">Основное</div>

                  <label className="podcast-editor-v3__field">
                    <span>Название подкаста *</span>
                    <input value={form.title} onChange={setField("title")} placeholder="Например: Тихая голова" />
                  </label>

                  <label className="podcast-editor-v3__field">
                    <span>Короткое описание</span>
                    <textarea value={form.shortDescription} onChange={setField("shortDescription")} rows={3} placeholder="Коротко: о чём подкаст и кому он поможет" />
                  </label>

                  <label className="podcast-editor-v3__field">
                    <span>Полное описание</span>
                    <textarea value={form.description} onChange={setField("description")} rows={5} placeholder="Расскажите, что получит слушатель, какие темы раскрываются и как часто выходят выпуски…" />
                  </label>

                  <div className="podcast-editor-v3__fields-row">
                    <label className="podcast-editor-v3__field">
                      <span>Категория</span>
                      <select value={form.topic || ""} onChange={setField("topic")}>
                        <option value="">Выберите категорию</option>
                        {TOPIC_OPTIONS.map((topic) => <option key={topic} value={topic}>{topic}</option>)}
                      </select>
                    </label>
                    <label className="podcast-editor-v3__field">
                      <span>Язык</span>
                      <select value={form.language || "Русский"} onChange={setField("language")}>
                        {LANGUAGE_OPTIONS.map((language) => <option key={language} value={language}>{language}</option>)}
                      </select>
                    </label>
                  </div>

                  <div className="podcast-editor-v3__fields-row">
                    <label className="podcast-editor-v3__field">
                      <span>Автор</span>
                      <input value={form.authorName} onChange={setField("authorName")} placeholder={me?.name || me?.login || "Имя автора"} />
                    </label>
                    <label className="podcast-editor-v3__field">
                      <span>Периодичность</span>
                      <input value={form.releaseFrequency} onChange={setField("releaseFrequency")} placeholder="Еженедельно" />
                    </label>
                  </div>
                </section>

                <section className="podcast-editor-v3__card">
                  <div className="podcast-editor-v3__section-title">Теги подкаста</div>
                  <div className="podcast-editor-v3__tags">
                    {asArray(form.tags).length === 0 ? <span className="podcast-editor-v3__tag-empty">Добавьте теги подкаста</span> : null}
                    {asArray(form.tags).map((tag) => (
                      <span className="podcast-editor-v3__tag" key={tag}>#{tag}<button type="button" onClick={() => removeTag(tag)} aria-label={`Удалить тег ${tag}`}>×</button></span>
                    ))}
                  </div>
                  <div className="podcast-editor-v3__tag-form">
                    <input
                      value={tagInput}
                      onChange={(event) => setTagInput(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          addTag();
                        }
                      }}
                      placeholder="Добавить тег..."
                    />
                    <button type="button" onClick={() => addTag()}>+ Добавить</button>
                  </div>
                </section>

                <section className="podcast-editor-v3__card podcast-editor-v3__episodes-card">
                  <div className="podcast-editor-v3__episodes-head">
                    <div>
                      <div className="podcast-editor-v3__section-title">Эпизоды</div>
                      <p className="podcast-editor-v3__hint">Эпизоды добавляются только после создания подкаста, чтобы каждый выпуск был привязан к своему шоу.</p>
                    </div>
                    <button type="button" className="podcast-editor-v3__mint-btn" disabled={!isEditing || saving || loading} onClick={() => openEpisodeBuilder()}>
                      + Добавить эпизод
                    </button>
                  </div>

                  {!isEditing ? (
                    <div className="podcast-editor-v3__episodes-empty">
                      <div>🎙️</div>
                      <strong>Сначала сохраните подкаст</strong>
                      <p>После сохранения здесь появится список выпусков и кнопка добавления первого эпизода.</p>
                    </div>
                  ) : null}

                  {isEditing && episodesLoading ? <div className="podcasts-empty podcasts-empty--inline">Загружаем эпизоды…</div> : null}

                  {isEditing && !episodesLoading ? (
                    episodes.length ? (
                      <div className="ep-editor-list">
                        {episodes.map((episode) => (
                          <article className="ep-editor-item" key={`podcast-episode-${episode.id || episode.title}`}>
                            <div className="ep-editor-drag" aria-hidden="true">⋮⋮</div>
                            <div className="ep-editor-num">{String(episode.status).toUpperCase() === "PUBLISHED" ? episode.number : "—"}</div>
                            <div className="podcast-editor-v3__episode-cover">
                              {episode.coverImageUrl ? <img src={episode.coverImageUrl} alt="" /> : <span>{episode.coverEmoji}</span>}
                            </div>
                            <div className="ep-editor-info">
                              <div className="ep-editor-title">{episode.title}</div>
                              <div className="ep-editor-meta">
                                <span>{formatDuration(episode.duration)}</span>
                                <span className="episode-dot" />
                                <span>{formatDate(episode.date)}</span>
                                <span className="episode-dot" />
                                <span className={`status-dot ${episode.status === "PUBLISHED" ? "status-published" : "status-draft"}`}>
                                  {episode.status === "PUBLISHED" ? "● Опубликован" : "● Черновик"}
                                </span>
                                {episode.listens ? <><span className="episode-dot" /><span>{formatInt(episode.listens)} прослуш.</span></> : null}
                              </div>
                            </div>
                            <div className="ep-editor-actions">
                              <button className="ep-icon-btn" type="button" title="Редактировать" onClick={() => openEpisodeBuilder(episode)}>✎</button>
                            </div>
                          </article>
                        ))}
                      </div>
                    ) : (
                      <div className="podcast-editor-v3__episodes-empty">
                        <div>🎙️</div>
                        <strong>Нет эпизодов</strong>
                        <p>Добавьте первый выпуск к этому подкасту.</p>
                        <button type="button" className="podcast-editor-v3__mint-btn" onClick={() => openEpisodeBuilder()}>
                          Добавить эпизод
                        </button>
                      </div>
                    )
                  ) : null}
                </section>
              </div>

              <aside className="podcast-editor-v3__right">
                <section className="podcast-editor-v3__card podcast-editor-v3__cover-card">
                  <div className="podcast-editor-v3__section-title">Обложка подкаста</div>
                  <button type="button" className={`podcast-editor-v3__cover-drop ${coverPreview ? "has-cover" : ""}`} onClick={() => coverInputRef.current?.click()}>
                    {coverPreview ? <img src={coverPreview} alt="Обложка подкаста" /> : (
                      <>
                        <span className="podcast-editor-v3__cover-icon" aria-hidden="true"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" stroke="none"/><path d="M21 15l-5-5L5 21"/></svg></span>
                        <span>Загрузить обложку<br /><small>JPG, PNG · 1:1 · до 5 МБ</small></span>
                      </>
                    )}
                  </button>
                  <input ref={coverInputRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(event) => uploadCoverFile(event.target.files?.[0])} />
                  <p className="podcast-editor-v3__micro-hint">
                    {uploadingCover ? "Загружаем обложку…" : coverFileName || "Рекомендуем квадратную обложку 3000×3000 px."}
                  </p>
                  <label className="podcast-editor-v3__field podcast-editor-v3__field--compact">
                    <span>Или ссылка на обложку</span>
                    <input value={form.coverUrl} onChange={setField("coverUrl")} placeholder="https://.../cover.webp" />
                  </label>
                  <label className="podcast-editor-v3__field podcast-editor-v3__field--compact">
                    <span>Эмодзи, если нет обложки</span>
                    <input value={form.coverEmoji} maxLength={8} onChange={setField("coverEmoji")} placeholder="🎙️" />
                  </label>
                </section>

                {isEditing ? (
                  <section className="podcast-editor-v3__card podcast-editor-v3__stats-card">
                    <div className="podcast-editor-v3__section-title">Статистика</div>
                    <div className="podcast-editor-v3__stats-list">
                      <div><span>Эпизодов</span><strong>{formatInt(podcast?.episodesCount || episodes.length)}</strong></div>
                      <div><span>Подписчиков</span><strong>{formatInt(podcast?.subscribersCount)}</strong></div>
                      <div><span>Прослушиваний</span><strong>{formatInt(podcast?.listensCount)}</strong></div>
                      <div><span>Обновлён</span><strong>{formatDate(podcast?.updatedAt)}</strong></div>
                    </div>
                  </section>
                ) : null}

                <section className="podcast-editor-v3__card podcast-editor-v3__publish-card">
                  <div className="podcast-editor-v3__section-title">Статус подкаста</div>
                  <label className="podcast-editor-v3__radio">
                    <input type="radio" name="podcastStatus" value="PUBLISHED" checked={form.status === "PUBLISHED"} onChange={() => setForm((current) => ({ ...current, status: "PUBLISHED", showInCatalog: true }))} />
                    <span><strong>Активен</strong><small>Подкаст виден в каталоге и может получать эпизоды</small></span>
                  </label>
                  <label className="podcast-editor-v3__radio">
                    <input type="radio" name="podcastStatus" value="DRAFT" checked={form.status === "DRAFT"} onChange={() => setForm((current) => ({ ...current, status: "DRAFT", showInCatalog: false }))} />
                    <span><strong>Черновик</strong><small>Подкаст скрыт от слушателей</small></span>
                  </label>
                  <label className="podcast-editor-v3__check">
                    <input type="checkbox" checked={form.showInCatalog && published} disabled={!published} onChange={setField("showInCatalog")} />
                    <span>Показывать в каталоге</span>
                  </label>
                  <div className="podcast-editor-v3__publish-actions">
                    <button className="podcast-editor-v3__mint-btn" type="button" disabled={saving || loading || uploadingCover} onClick={() => savePodcast(form.status)}>{saving ? "Сохраняем..." : "Сохранить подкаст"}</button>
                    <button className="podcast-editor-v3__ghost-btn" type="button" disabled={saving || loading || uploadingCover} onClick={() => savePodcast("DRAFT")}>Сохранить черновик</button>
                    {!isEditing ? (
                      <button className="podcast-editor-v3__ghost-btn" type="button" disabled={saving || loading || uploadingCover} onClick={() => savePodcast(form.status, { addEpisodeAfter: true })}>Сохранить и добавить эпизод</button>
                    ) : (
                      <button className="podcast-editor-v3__ghost-btn" type="button" disabled={saving || loading} onClick={() => openEpisodeBuilder()}>Добавить эпизод</button>
                    )}
                    <button className="podcast-editor-v3__plain-btn" type="button" onClick={() => navigate("/dashboard/podcasts")}>Отмена</button>
                  </div>
                </section>

                <section className="podcast-editor-v3__card podcast-editor-v3__checklist-card">
                  <div className="podcast-editor-v3__section-title">Проверка</div>
                  <ul>
                    <StatusCheck ok={form.title}>Название подкаста заполнено</StatusCheck>
                    <StatusCheck ok={form.shortDescription || form.description}>Описание заполнено</StatusCheck>
                    <StatusCheck ok={form.topic}>Категория выбрана</StatusCheck>
                    <StatusCheck ok={form.coverUrl || form.coverEmoji}>Обложка или эмодзи указаны</StatusCheck>
                    <StatusCheck ok={isEditing}>Подкаст создан, можно добавлять эпизоды</StatusCheck>
                  </ul>
                </section>

                <section className="podcast-editor-v3__aegis-card">
                  <div><span /> Aegis · Активно</div>
                  <p>Подкаст будет доступен подписчикам платформы. Эпизоды добавляются после сохранения карточки подкаста.</p>
                </section>
              </aside>
            </div>
          </main>
        </div>
      </div>
      {limitModal}
    </div>
  );
}
